// The one question card, first version: what an agent asks, one card
// whoever asks. Ported from today's QuestionCard (code/TranscriptItems.tsx).
//
// - A prompt with choices takes one, or several when it says so, or the
//   person's own words in "Other", instead of the choices, unless it takes
//   its choices only, as an agent's permission does: it then has no "Other".
//   A prompt with no choices takes words only. Blank words, in a prompt in
//   words only or in "Other" picked and left empty, leave the prompt
//   unanswered, as an agent's form field left out, unless it is required.
//   The asker says which are: MCP's `ask` makes every prompt required
//   unless the agent says otherwise. A
//   prompt that takes a number or a whole number offers a numeric keyboard,
//   in a text input whose raw words are checked as the server reads them.
//   The card sends only the replies the server takes (`wordsMisfit`, the
//   model's rule).
// - It answers with the version it shows. Online the card waits for the
//   server; offline its saved answer shows as pending until replay settles.
//   A refusal says why, in the server's words.
// - Outside the dock, which says them in its head, it says whether the
//   question is urgent, whether its agent waits, and when it was last
//   revised (questions/waiting.tsx); the flags only when no head above it
//   says them already, as the board's strip does.
// - Once its expiry has passed, it says when its agent stopped waiting and
//   what it went on with, and its button says "Answer anyway": the answer
//   still reaches the agent (questions/waiting.tsx).
// - When the question changes under it, by a revision the feed brings or one
//   that a stale refusal reveals, the card shows the new version, clears what
//   was picked for the old one, and says so.
// - What an agent asks in its own words, and what its choices lead to, is
//   Markdown, rendered as the record's is, but a link shows where it leads
//   beside its words. A permission's main line and its tool detail stay
//   literal (permissionText.ts).
// - Docked in a session's pane (code/QuestionDock.tsx), the dock draws its
//   head above it and gives the start of its foot, and what it asks scrolls
//   between them. The card still names the issue the question is about,
//   where a plan to approve can be read.
import { useId, useState, type KeyboardEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Issue, Prompt, Question, Reply, Session } from '../api/types'
import { CheckIcon } from '../components/Icons'
import { Markdown } from '../markdown/Markdown'
import { useSessionPath } from '../projects/ProjectRoute'
import { useBoardState, useStore } from '../store/context'
import { wordsMisfit } from '../vocab'
import { permissionText } from './permissionText'
import { expired, expiredText, QuestionFlags, QuestionRevised, useExpiryClock, waits } from './waiting'

/** What the person picked for one prompt. */
interface Pick {
  /** The choices, by their index. */
  choices: number[]
  /** Their own words. */
  words: string
  /** Their words are the reply: "Other" is picked. */
  own: boolean
}

const NONE: Pick = { choices: [], words: '', own: false }

/**
 * The reply a pick gives its prompt, as the server takes it; null while it
 * gives none. Words are the reply on a prompt in words only, or in "Other";
 * those the prompt does not take give none (`wordsMisfit`). Blank words are
 * taken where the prompt is not required, and leave it unanswered. With no
 * choice picked and "Other" not picked, a prompt with choices has no reply.
 */
export function replyOf(prompt: Prompt, pick: Pick): Reply | null {
  const words = pick.words.trim()
  if (prompt.choices.length === 0 || pick.own) {
    if (wordsMisfit(prompt, words)) return null
    return { kind: 'text', value: words }
  }
  if (pick.choices.length === 0) return null
  return prompt.multi_select ? { kind: 'choices', value: [...pick.choices].sort((a, b) => a - b) } : { kind: 'choice', value: pick.choices[0]! }
}

/** What a reply says: the choices' labels, or the words. */
export function replyText(prompt: Prompt | undefined, reply: Reply | undefined): string {
  if (!reply) return ''
  const label = (choice: number) => prompt?.choices[choice]?.label ?? ''
  if (reply.kind === 'text') return reply.value
  if (reply.kind === 'choice') return label(reply.value)
  return reply.value.map(label).join(', ')
}

/** What an agent asks in its own words, rendered; remote images stay out, as in its replies, and a link shows its address. */
function Asked({ text }: { text: string }) {
  return <Markdown source={text} className="question-text" localImagesOnly linkTargets />
}

/** A permission's short main line, as it stands; an agent's own question, rendered. */
function promptTitle(prompt: Prompt): ReactNode {
  return prompt.choices_only ? permissionText(prompt).title : <Asked text={prompt.text} />
}

/** The link to a question's issue. */
function IssueLink({ issue }: { issue: Issue }) {
  return (
    <Link className="request-issue" to={`/${encodeURIComponent(issue.key)}`}>
      <span className="request-issue-key">{issue.key}</span>
      <span className="request-issue-title">{issue.title}</span>
    </Link>
  )
}

/** A question in play; a card of its own names its issue, the session that asked it and, among several, its project. */
export function QuestionCard({
  question,
  issue,
  source,
  project,
  docked = false,
  flags = true,
  foot,
}: {
  question: Question
  issue?: Issue
  source?: Session | null
  project?: string
  /** Under the dock's head, in place of its own top line. */
  docked?: boolean
  /** Outside the dock, whether it says "Urgent" and "The agent waits": not under a head that says them. */
  flags?: boolean
  /** Before the foot's hint and its button. */
  foot?: ReactNode
}) {
  const store = useStore()
  const { pendingAnswers } = useBoardState()
  const pathOf = useSessionPath()
  const titleId = useId()
  const [picks, setPicks] = useState<readonly Pick[]>(() => question.prompts.map(() => NONE))
  /** The version the card shows: what it answers. */
  const [shown, setShown] = useState(question.version)
  const [changed, setChanged] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const now = useExpiryClock([question])

  // A new version: what was picked was for the prompts before it.
  if (question.version !== shown) {
    setShown(question.version)
    setPicks(question.prompts.map(() => NONE))
    setChanged(true)
    setError(null)
  }

  if (question.state === 'answered') {
    const answered = <AnsweredQuestion question={question} pending={pendingAnswers.has(question.id)} />
    return docked ? <div className="question-card-body">{answered}</div> : answered
  }
  if (question.state !== 'open') return null

  const { prompts } = question
  const several = prompts.length > 1
  const pickOf = (i: number) => picks[i] ?? NONE
  const change = (i: number, next: Pick) => setPicks((held) => prompts.map((_, at) => (at === i ? next : (held[at] ?? NONE))))
  const replies = prompts.map((prompt, i) => replyOf(prompt, pickOf(i)))
  const complete = replies.every((reply) => reply !== null)
  const late = expired(question, now)

  const submit = async () => {
    if (!complete || sending) return
    setSending(true)
    setError(null)
    setChanged(false)
    const outcome = await store.answer(question, replies as Reply[])
    setSending(false)
    if (outcome.ok) return
    if (outcome.stale) setChanged(true)
    else setError(`${several ? 'Could not send the answers' : 'Could not send the answer'}: ${outcome.message}`)
  }
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      void submit()
    }
  }

  // What it asks; docked, it scrolls between the dock's head and the foot.
  const body = (
    <>
      {/* Docked, the head says who asks: several prompts need no title of their own to see. */}
      {several || prompts[0]?.choices_only ? (
        <h3 className={docked && several ? 'visually-hidden' : 'request-title'} id={titleId}>
          {several ? 'The session asks' : prompts[0] && permissionText(prompts[0]).title}
        </h3>
      ) : (
        // Its own words may run to paragraphs and lists, which a heading cannot hold.
        <div role="heading" aria-level={3} className="request-title" id={titleId}>
          {prompts[0] && <Asked text={prompts[0].text} />}
        </div>
      )}
      {source !== undefined && (
        <p className="request-meta">
          <Link to={pathOf(question.session)}>{source?.title ?? source?.agent.model ?? 'Session'}</Link>
        </p>
      )}
      {!docked && (question.revised_at || (flags && (question.urgent || waits(question, now)))) && (
        <p className="request-meta question-terms">
          {flags && <QuestionFlags question={question} now={now} />}
          <QuestionRevised question={question} now={now} first />
        </p>
      )}
      {docked && issue && (
        <p className="request-meta">
          <IssueLink issue={issue} />
        </p>
      )}
      {changed && (
        <p className="request-note question-changed" role="status">
          This question changed: read it again.
        </p>
      )}
      {question.reopened && (
        <p className="request-note question-error" role="status">
          {question.reopened}
        </p>
      )}
      {question.expiry && late && (
        <p className="request-note question-expired" role="status">
          {expiredText(question.expiry, now)}
        </p>
      )}
      {prompts.map((prompt, i) =>
        prompt.choices.length > 0 ? (
          <Choices key={`${shown}-${i}`} prompt={prompt} several={several} pick={pickOf(i)} disabled={sending} onChange={(next) => change(i, next)} />
        ) : (
          <Words key={`${shown}-${i}`} prompt={prompt} several={several} pick={pickOf(i)} disabled={sending} onChange={(next) => change(i, next)} />
        ),
      )}
      {error && (
        <p className="request-note question-error" role="alert">
          {error}
        </p>
      )}
    </>
  )
  return (
    <article className="request-card question-card" aria-labelledby={titleId} onKeyDown={onKeyDown}>
      {!docked && (
        <div className="request-top">
          {project && <span className="request-project">{project}</span>}
          <span className="kind-chip">Question</span>
          {issue && <IssueLink issue={issue} />}
        </div>
      )}
      {docked ? <div className="question-card-body">{body}</div> : body}
      <div className="form-actions">
        {foot}
        <span className="muted form-hint form-hint-keys">⌘↵ to send</span>
        <button type="button" className="button button-primary" disabled={!complete || sending} onClick={() => void submit()}>
          {sending ? 'Sending…' : late ? 'Answer anyway' : several ? 'Send answers' : 'Send answer'}
        </button>
      </div>
    </article>
  )
}

interface FieldProps {
  prompt: Prompt
  several: boolean
  pick: Pick
  disabled: boolean
  onChange: (pick: Pick) => void
}

/** A prompt's choices, one or several, and "Other" in the person's own words, which replaces them, unless it takes its choices only. */
function Choices({ prompt, several, pick, disabled, onChange }: FieldProps) {
  const name = useId()
  const multi = prompt.multi_select
  const type = multi ? 'checkbox' : 'radio'
  const choose = (choice: number, on: boolean) => {
    const others = multi ? pick.choices.filter((c) => c !== choice) : []
    onChange({ ...pick, own: false, choices: on ? [...others, choice] : others })
  }
  return (
    <fieldset className="code-question" aria-label={permissionText(prompt).title} disabled={disabled}>
      {(several || multi) && (
        <legend>
          {several && <span className="code-question-text">{promptTitle(prompt)}</span>}
          {multi && <span className="muted">Choose any</span>}
        </legend>
      )}
      {permissionText(prompt).detail && <pre className="permission-detail"><code>{permissionText(prompt).detail}</code></pre>}
      {prompt.choices.map((choice, c) => (
        <label key={c} className="code-choice">
          <input type={type} name={name} checked={!pick.own && pick.choices.includes(c)} onChange={(event) => choose(c, event.target.checked)} />
          <span className="code-choice-text">
            <span className="code-choice-label">
              {choice.label}
              {choice.recommended && <span className="code-choice-recommended">Recommended</span>}
            </span>
            <span className="code-choice-detail">
              <Asked text={choice.description} />
            </span>
          </span>
        </label>
      ))}
      {!prompt.choices_only && (
        <label className="code-choice code-choice-own">
          <input
            type={type}
            name={name}
            checked={pick.own}
            aria-label="Other"
            onChange={(event) => onChange({ ...pick, own: event.target.checked, choices: event.target.checked ? [] : pick.choices })}
          />
          <input
            {...wordsInput(prompt)}
            className="code-choice-input"
            value={pick.words}
            maxLength={2000}
            aria-label={several ? `Your own answer to: ${prompt.text}` : 'Your own answer'}
            placeholder="Other, in your own words"
            onChange={(event) => {
              // Words of their own are their answer: the choices give way to them,
              // and "Other" stays picked when they are erased.
              const words = event.target.value
              onChange({ words, own: true, choices: [] })
            }}
          />
        </label>
      )}
    </fieldset>
  )
}

/**
 * The input a prompt's own words go in: always text, with a numeric keyboard
 * for a number or a whole number. A number input would hide what the person
 * typed when it reads as no number ("-", "e", a number too large), and pass
 * it on as a blank; the raw words are checked instead (`wordsMisfit`).
 */
function wordsInput(prompt: Prompt): { type: string; inputMode?: 'decimal' | 'numeric' } {
  if (prompt.words === 'number') return { type: 'text', inputMode: 'decimal' }
  if (prompt.words === 'integer') return { type: 'text', inputMode: 'numeric' }
  return { type: 'text' }
}

/** A prompt with no choices: answered in words, or in a number when it takes one. */
function Words({ prompt, several, pick, disabled, onChange }: FieldProps) {
  const label = several ? `Your answer to: ${prompt.text}` : 'Your answer'
  const required = prompt.required ?? false
  return (
    <div className="code-question">
      {several && (
        <div className="code-question-text">
          <Asked text={prompt.text} />
        </div>
      )}
      {prompt.words === 'number' || prompt.words === 'integer' ? (
        <input
          {...wordsInput(prompt)}
          className="input question-words"
          value={pick.words}
          disabled={disabled}
          required={required}
          aria-label={label}
          placeholder={prompt.words === 'integer' ? 'A whole number' : 'A number'}
          onChange={(event) => onChange({ ...pick, words: event.target.value })}
        />
      ) : (
        <textarea
          className="textarea question-words"
          value={pick.words}
          rows={3}
          maxLength={4000}
          disabled={disabled}
          required={required}
          aria-label={label}
          placeholder="Your answer"
          onChange={(event) => onChange({ ...pick, words: event.target.value })}
        />
      )}
    </div>
  )
}

/** A question once answered: one line, and what each prompt was answered. */
function AnsweredQuestion({ question, pending }: { question: Question; pending: boolean }) {
  const replies = question.answer?.replies ?? []
  return (
    <div className="code-settled is-answered">
      <CheckIcon size={12} />
      <div className="code-settled-what">
        <strong>Answered</strong>
        {pending && <p className="muted" role="status">Sent when the server is back.</p>}
        <dl className="code-answers">
          {question.prompts.map((prompt, i) => (
            <div key={i}>
              <dt>{permissionText(prompt).title}</dt>
              <dd>{replyText(prompt, replies[i])}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}
